import type { AircraftState, AirportData, EnvState, RealismLevel, RealismOptions, RunwayConfig } from '@atc/core';
import type { Aircraft, AircraftEvent, Wind } from './aircraft';
import type { Instruction, SystemCommand } from './commands';
import type { RadioMessage } from './comms';
import type { EmergencyNature, EmergencyRate } from './emergency';
import type { Stats } from './scoring';
import type { AircraftSpec } from './spawner';
import type { PilotMemory } from './pilot-brain';
import type { RequestKind } from './patience';
import type { AnyPilotBrain, PilotDecision } from './brain/types';
import type { BrainLogEntry } from './brain/host';
import type { CoordinationRequest, OperationsSnapshot } from './operations';
import type { Impact } from './collision';

export const STEP_S = 0.25;
export const FIRST_CHECKIN_DELAY_S = 8;
export const DEFAULT_CHECKIN_SPACING_S = 12;
export const OPENING_QUIET_S = { arrivals: 60, departures: 120 } as const;

export interface SimOptions {
  position?: 'legacy' | 'tower';
  /** Academy radar role: runway clearances belong to a scripted tower colleague. */
  academyApproach?: boolean;
  coaching?: boolean;
  realism?: RealismLevel | RealismOptions;
  seed?: number;
  scopeRadiusNm?: number;
  arrivalsPerHour?: number;
  departuresPerHour?: number;
  maxAircraft?: number;
  initialTraffic?: boolean;
  initialArrivals?: number;
  initialDepartures?: number;
  checkInSpacingS?: number;
  wind?: Wind;
  /** Gusting, shifting wind even at a realism level that holds it steady (storm weather). */
  gustyWind?: boolean;
  trafficRamp?: boolean;
  trainingReadback?: boolean;
  pilotInitiative?: 'essential' | 'normal';
  shortCallsigns?: boolean;
  pilotPatience?: boolean;
  brain?: AnyPilotBrain;
  fuel?: boolean;
  emergencies?: EmergencyRate;
  activeRunways?: { arrivals: string[]; departures: string[] };
}

export type SimEvent =
  | AircraftEvent
  | { type: 'spawn'; ac: Aircraft }
  | { type: 'removed'; ac: Aircraft }
  | { type: 'conflictStart'; a: Aircraft; b: Aircraft; wake: boolean }
  | { type: 'conflictEnd'; a: Aircraft; b: Aircraft }
  | { type: 'conflictWarning'; a: Aircraft; b: Aircraft; inS: number }
  | ({ type: 'collision' } & Impact)
  | { type: 'command'; ac: Aircraft; instructions: Instruction[] }
  | { type: 'unable'; ac: Aircraft; instruction: Instruction; reason: string }
  | { type: 'pilotQuery'; ac: Aircraft; subject: string }
  | { type: 'pilotWaiting'; ac: Aircraft; stage: 1 | 2 | 3; need: RequestKind; waitedS: number; action?: 'hold' | 'returnToStand'; fix?: string }
  | { type: 'emergencyDeclared'; ac: Aircraft; nature: EmergencyNature }
  | { type: 'emergencyAcknowledged'; ac: Aircraft }
  | { type: 'minimumFuel'; ac: Aircraft; minutesLeft: number }
  | { type: 'fuelEmergency'; ac: Aircraft; minutesLeft: number }
  | { type: 'forcedLanding'; ac: Aircraft }
  | { type: 'realism'; level: RealismLevel }
  | { type: 'academyCall'; callsign: string; text: string; request?: RequestKind }
  | { type: 'academyTowerHandoff'; ac: Aircraft };

export interface CommandResult {
  ok: boolean;
  message: string;
  system?: SystemCommand;
  text?: string;
  status?: 'accepted' | 'partial' | 'rejected';
  accepted?: Instruction[];
  rejected?: Instruction[];
}

export interface SimEvents {
  tick: (states: AircraftState[], simTimeS: number) => void;
  radio: (msg: RadioMessage) => void;
  event: (event: SimEvent) => void;
  message: (text: string) => void;
}

export interface DebriefEvent { timeS: number; type: string; callsigns: string[]; detail: string }

export type AircraftContext = Readonly<Omit<AircraftState, 'assigned' | 'phase' | 'airline' | 'kind'> & {
  assigned: Readonly<Aircraft['assigned']>;
  phase: Aircraft['phase'];
  airline: string;
  kind: Aircraft['kind'];
  approachRunway?: string;
  established: boolean;
  landingCleared: boolean;
  departureRunway?: string;
  occupiedRunway?: string;
}>;

export interface Sim {
  replay(): { timeS: number; aircraft: AircraftState[] }[];
  practiceAt(timeS: number): boolean;
  readonly practicing: boolean;
  operations(): OperationsSnapshot;
  coordinate(request: CoordinationRequest): CommandResult;
  readonly airport: AirportData;
  readonly simTimeS: number;
  timeScale: number;
  paused: boolean;
  readonly realism: RealismOptions;
  /** Academy approach position: the player clears approaches and transfers to tower; tower clears to land. */
  readonly approachRole: boolean;
  readonly env: EnvState;
  runwayConfig(): RunwayConfig;
  setBrain(brain: AnyPilotBrain): void;
  readonly brainId: string;
  deliverDecision(id: number, decision: unknown, usage?: Record<string, number>): void;
  brainLog(): readonly BrainLogEntry[];
  step(wallDtS: number): void;
  command(line: string): CommandResult;
  clearance(proposal: unknown): CommandResult;
  transmission(proposal: unknown): CommandResult;
  pilotContext(callsign: string): { state: AircraftContext; memory: PilotMemory; simTimeS: number } | null;
  configurePilots(options: { initiative?: 'essential' | 'normal'; shortCallsigns?: boolean }): void;
  timeline(): readonly DebriefEvent[];
  aircraft(): AircraftState[];
  context(): readonly AircraftContext[];
  internal(): readonly Aircraft[];
  find(callsign: string): Aircraft | undefined;
  stats(): Stats;
  statsText(): string;
  setRealism(level: RealismLevel | RealismOptions): void;
  /** Airport local hour at sim time zero, so pilots greet by time of day. */
  setLocalClock(startHour: number): void;
  /** Change the prevailing wind mid-session (weather change). Runways in use are not re-chosen. */
  setWind(wind: Wind, gusty?: boolean): void;
  on<K extends keyof SimEvents>(event: K, handler: SimEvents[K]): () => void;
  radioLog(): readonly RadioMessage[];
  addAircraft(spec: AircraftSpec): Aircraft;
  declareEmergency(callsign: string, nature: EmergencyNature): boolean;
  enableTrainingReadback(): void;
  /** Deterministic Academy radio/coordination cue, delivered through the normal radio log. */
  academyCall(callsign: string | null, text: string, request?: RequestKind, repeatAs?: string): void;
  /** Stage a departure launched by the tower colleague before an Academy approach exercise. */
  academyLaunchDeparture(callsign: string, initialAltitudeFt: number): boolean;
  /** Tower colleague instructs a missed approach and reports it to the Academy player. */
  academyTowerGoAround(callsign: string, reason: string): boolean;
  /** Stage an AI colleague's approach clearance without the player's compliance delay. */
  academyPrepareArrival(callsign: string, runway: string): boolean;
  /** Mark controller transmissions made until this is turned off as spoken on the microphone (RadioMessage.voiced). */
  setControllerVoiced(on: boolean): void;
  sayAgain(callsign?: string, detail?: 'speed'): void;
}
